import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DataViewModule } from 'primeng/dataview';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { Menu, MenuModule } from 'primeng/menu';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { APP_ROUTING } from '../../../app-routing.constants';
import { AuthService } from '../../../login-service/auth-service';
import { basicDetails } from '../../../models/basicDetails';
import { ExamDetails } from '../../../models/exam';
import { QuestionCategory, Questions } from '../../../models/questionBank';
import { Subject } from '../../../models/subjects';
import {
  EXAM_ADD_NEW_QUESTIONS_CONFIG,
  EXAM_SECTION_CONFIG,
  EXAM_SECTION_QUESTIONS_CONFIG,
  EXAM_TEMPLATE_CONFIG,
  TemplateColumnConfig,
} from '../../../models/template-column-config';
import { CrudService } from '../../../services/crud-app.service';
import { DownloadTemplateComponent } from '../../download-template/download-template.component';
import { UploadTemplateComponent } from '../../upload-template/upload-template.component';

@Component({
  selector: 'app-examlisting',
  standalone: true,
  imports: [
    ToastModule,
    TableModule,
    InputTextModule,
    CardModule,
    ButtonModule,
    FormsModule,
    CommonModule,
    DataViewModule,
    DropdownModule,
    DownloadTemplateComponent,
    UploadTemplateComponent,
    TooltipModule,
    ConfirmDialogModule,
    MenuModule,
  ],
  templateUrl: './exam-listing.component.html',
  styleUrl: './exam-listing.component.scss',
  providers: [MessageService],
})
export class ExamListingComponent implements OnInit {
  readonly isStudent = this.authService.isStudent;
  examDetails: ExamDetails[] = [];
  loading: boolean = true;
  teachers: basicDetails[] = [];
  subjectList: Subject[] = [];
  @ViewChild('globalMenu') globalMenu!: Menu;
  @ViewChild('dt2') table!: Table;
  currentMenuItems: any = [];
  globalSearchQuery: string = '';
  selectedSort: string = 'nameAsc';
  originalExamDetails: ExamDetails[] = [];
  sortOptions: SelectItem[] = [
    { label: 'Name (A-Z)', value: 'nameAsc' },
    { label: 'Name (Z-A)', value: 'nameDesc' },
    { label: 'Creation Date (Newest)', value: 'dateDesc' },
    { label: 'Creation Date (Oldest)', value: 'dateAsc' },
  ];
  columnGap: number = 0;
  sheets: any[] = [];
  allQuestions: Questions[] = [];
  questionCategory: QuestionCategory[] = [];
  selectedCourseId: string | null = null;
  profileId: string | null = null;
  courseId: string | null = '';

  constructor(
    private crud: CrudService,
    private router: Router,
    private readonly confirmationService: ConfirmationService,
    private messageService: MessageService,
    private authService: AuthService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.courseId = this.route.snapshot.paramMap.get('id');
    if (this.authService.isAdmin() && this.courseId) {
      this.authService.setSelectedCourseId(this.courseId);
    }
    this.initData();
  }

  private async initData(): Promise<void> {
    this.profileId = this.authService.profileId();
    this.selectedCourseId = this.authService.getSelectedCourseId();

    if (!this.authService.isStudent()) {
      this.teachers = <basicDetails[]>await this.crud.get('teacher/profile');
      this.subjectList = <Subject[]>await this.crud.get('topics');
      this.allQuestions = <Questions[]>await this.crud.get('questions');
      this.questionCategory = <QuestionCategory[]>await this.crud.get('question-category');
      this.buildSheets();
    }
    this.getExamDetails();
  }

  get filteredExamDetails(): ExamDetails[] {
    if (!this.examDetails) return [];

    // For students, filter by isPublish = true
    if (this.authService.isStudent()) {
      return this.examDetails.filter((exam) => exam.isPublish === true);
    }

    // For teachers/admins, return all exams
    return this.examDetails;
  }

  async getExamDetails() {
    try {
      let examsToLoad: any[] = [];
      // if (this.authService.isAdmin()) {
      //   examsToLoad = await this.crud.get('exams');
      // } else
      if (this.selectedCourseId) {
        let url = `exams/by-course/${this.selectedCourseId}`;
        if (this.authService.isStudent() && this.profileId) {
          url += `?studentId=${this.profileId}`;
        }
        examsToLoad = await this.crud.get(url);
      } else {
        this.examDetails = [];
        this.loading = false;
        return;
      }

      this.examDetails = examsToLoad.sort(
        (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
      );
      this.examDetails = examsToLoad;
      this.originalExamDetails = [...examsToLoad];
      this.loading = false;
      this.applyFilters();
    } catch (error) {
      console.error('Failed to load exams', error);
      this.loading = false;
    }
  }

  applyFilters() {
    const searchLower = this.globalSearchQuery?.toLowerCase();

    let filteredData = [...this.originalExamDetails];

    if (this.authService.isStudent()) {
      filteredData = filteredData.filter((exam) => exam.isPublish === true);
    }

    if (searchLower) {
      filteredData = filteredData.filter(({ name }) => name.toLowerCase().includes(searchLower));
    }

    filteredData.sort((a, b) => {
      switch (this.selectedSort) {
        case 'nameAsc':
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
        case 'nameDesc':
          return b.name.localeCompare(a.name, undefined, { numeric: true, sensitivity: 'base' });
        default:
          return 0;
      }
    });

    this.examDetails = filteredData;
  }

  onFilterChange() {
    this.applyFilters();
  }

  getTeacherNames(teachers: any[]): string {
    if (!teachers || teachers.length === 0) {
      return 'No teachers assigned';
    }
    return teachers.map((teacher) => teacher.firstName + ' ' + teacher.lastName).join(', ');
  }

  getExamSubjects(exam: any): string {
    if (!exam || !exam.sections || exam.sections.length === 0) {
      return 'No subjects';
    }

    const subjects = exam.sections
      .map((section: any) => section.subject)
      .filter((subject: any) => subject && subject.name)
      .map((subject: any) => subject.name);

    if (subjects.length === 0) {
      return 'No subjects';
    }
    return [...new Set(subjects)].join(', ');
  }

  isExamStartDateValid(exam: any): boolean {
    if (!exam.startDate) {
      return false;
    }
    const startDate = new Date(exam.startDate);
    const currentDate = new Date();
    return currentDate >= startDate;
  }

  isExamAvailable(exam: any): boolean {
    if (!exam.startDate) {
      return false;
    }
    const startDate = new Date(exam.startDate);
    const currentDate = new Date();
    const isAvailable = currentDate >= startDate;
    return isAvailable;
  }

  getTimeUntilExamStarts(exam: any): string {
    if (!exam.startDate) {
      return 'Publish date not set';
    }
    const startDate = new Date(exam.startDate);
    const currentDate = new Date();
    if (currentDate >= startDate) {
      return '';
    }
    const timeDiff = startDate.getTime() - currentDate.getTime();
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days} day(s), ${hours} hour(s)`;
    } else if (hours > 0) {
      return `${hours} hour(s), ${minutes} minute(s)`;
    } else {
      return `${minutes} minute(s)`;
    }
  }

  formatStartDate(startDate: string): string {
    if (!startDate) {
      return 'Not set';
    }
    const date = new Date(startDate);
    return date.toLocaleString();
  }

  clonedExamDetailss: { [s: string]: ExamDetails } = {};

  onRowEditInit(ExamDetails: ExamDetails) {
    this.clonedExamDetailss[ExamDetails.id] = { ...ExamDetails };
  }

  onAddNew() {
    this.router.navigate([APP_ROUTING['EXAM_APP'], 0]);
  }

  onEditExam(exam: any) {
    this.router.navigate([APP_ROUTING['EXAM_APP'], exam.id]);
  }

  isExamSubmitted(exam: any): boolean {
    return exam.isSubmitted === true;
  }

  canViewDashboard(exam: any): boolean {
    if (this.isExamSubmitted(exam)) return true;
    if (exam.deadlineDate && this.checkQuizDeadline(exam.deadlineDate)) return true;
    return false;
  }

  onTakeExam(exam: any) {
    if (this.authService.isStudent()) {
      if (this.isExamSubmitted(exam)) {
        this.messageService.add({
          severity: 'info',
          summary: 'Already Submitted',
          detail: 'You have already submitted this exam.',
          life: 4000,
        });
        return;
      }

      const isAvailable = this.isExamAvailable(exam);
      if (!isAvailable) {
        const timeRemaining = this.getTimeUntilExamStarts(exam);
        const startDate = this.formatStartDate(exam.startDate);

        this.messageService.add({
          severity: 'warn',
          summary: 'Exam Not Available',
          detail: `This exam will be available on ${startDate}. Time remaining: ${timeRemaining}`,
          life: 6000,
        });
        return;
      }
    }
    if (this.authService.isTeacher() || this.authService.isAdmin()) {
      this.router.navigate([APP_ROUTING['EXAM_INSTRUCTIONS'], exam.id], {
        queryParams: {
          previewMode: 'true',
          returnUrl: this.router.url,
        },
      });
    } else {
      this.router.navigate([APP_ROUTING['EXAM_INSTRUCTIONS'], exam.id]);
    }
  }

  onDeleteExam(examId: number) {
    const examToDelete = this.examDetails.find((exam) => exam.id === examId);

    if (!examToDelete) {
      this.showMessage('error', 'Error', 'Exam not found');
      return;
    }

    if (examToDelete.isPublish) {
      this.showMessage('warn', 'Cannot Delete', 'Published exams cannot be deleted');
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to delete the exam "${examToDelete.name}"?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        this.confirmationService.close();
        try {
          await this.crud.delete(`exams/${examId}`);
          this.getExamDetails();
          this.showMessage('success', 'Success', 'Exam deleted successfully');
        } catch (error) {
          this.showMessage('error', 'Error', 'An error occurred while deleting the exam');
          console.error('An error occurred while deleting the exam:', error);
        }
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  private showMessage(severity: string, summary: string, detail: string) {
    this.messageService.add({ severity, summary, detail });
  }

  isDeleteDisabled(exam: any): boolean {
    if (this.authService.isAdmin()) {
      return exam.isPublish;
    }
    return (
      exam.isPublish ||
      (this.authService.isTeacher() && !exam.teachers?.some((t: any) => t.id === this.profileId))
    );
  }

  getDeleteButtonTitle(exam: any): string {
    if (exam.isPublish) {
      return 'Published exams cannot be deleted';
    }
    if (this.authService.isTeacher() && !exam.teachers?.some((t: any) => t.id === this.profileId)) {
      return 'You can only delete exams you are assigned to';
    }
    return 'Delete Exam';
  }

  getExamTotalQuestions(exam: any): number {
    if (!exam || !exam.sections) {
      return 0;
    }
    return exam.sections.reduce((total: number, section: any) => {
      return total + (section.numberOfQuestions || 0);
    }, 0);
  }

  onRowEditSave(ExamDetails: ExamDetails) {}

  onRowEditCancel(ExamDetails: ExamDetails, index: number) {
    this.examDetails[index] = this.clonedExamDetailss[ExamDetails.id];
    delete this.clonedExamDetailss[ExamDetails.id];
  }

  onGlobalFilter(event: Event) {
    const input = event.target as HTMLInputElement;
    if (this.table) {
      this.table.filterGlobal(input.value, 'contains');
    }
  }

  customSort(event: any) {
    event.data.sort((a: any, b: any) => {
      const valA = a[event.field] ?? '';
      const valB = b[event.field] ?? '';
      const result = String(valA).localeCompare(String(valB), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return event.order * result;
    });
  }

  get teacherNames(): string[] {
    return this.teachers.map((t) => t.firstName + ' ' + t.lastName);
  }
  get subjectNames(): string[] {
    return this.subjectList.map((s) => s.name);
  }
  get questions(): string[] {
    return this.allQuestions.map((q) => q.title);
  }
  get questionCategoryNames(): string[] {
    return this.questionCategory.map((q) => q.name);
  }

  private buildSheets() {
    this.sheets = [
      {
        name: 'Exams',
        columns: EXAM_TEMPLATE_CONFIG,
        rows: EXAM_TEMPLATE_CONFIG,
        dropdowns: {
          Teachers: this.teacherNames,
        },
      },
      {
        name: 'Exam Sections',
        columns: EXAM_SECTION_CONFIG,
        rows: EXAM_SECTION_CONFIG,
        dropdowns: {
          Subject: this.subjectNames,
        },
      },
      {
        name: 'Section Questions',
        columns: EXAM_SECTION_QUESTIONS_CONFIG,
        rows: EXAM_SECTION_QUESTIONS_CONFIG,
        dropdowns: {
          'Question Title': this.questions,
        },
      },
      {
        name: 'Add New Questions',
        columns: EXAM_ADD_NEW_QUESTIONS_CONFIG,
        rows: EXAM_ADD_NEW_QUESTIONS_CONFIG,
        dropdowns: {
          Subject: this.subjectNames,
          Category: this.questionCategoryNames,
          'Section Name':
            this.sheets
              ?.find((s) => s.name === 'Exam Sections')
              ?.rows?.map((r: any) => r['Section Name']) || [],
        },
      },
    ];
  }

  handleParsedData(data: any) {
    const examsRaw = data['Exams'];
    const sectionsRaw = data['Exam Sections'];
    const sectionQuestionsRaw = data['Section Questions'];
    const addNewQuestionsRaw = data['Add New Questions'];

    const exams = this.parseSheet(examsRaw, EXAM_TEMPLATE_CONFIG);
    const sections = this.parseSheet(sectionsRaw, EXAM_SECTION_CONFIG);
    const sectionQuestions = this.parseSheet(sectionQuestionsRaw, EXAM_SECTION_QUESTIONS_CONFIG);
    const addNewQuestions = this.parseSheet(addNewQuestionsRaw, EXAM_ADD_NEW_QUESTIONS_CONFIG);

    if (exams.length > 0) {
      exams[0].sections = sections;
      exams[0].questions = sectionQuestions;
    }

    this.confirmationService.confirm({
      header: 'Upload Exams',
      message: `Are you sure you want to upload ${exams.length} exams?`,
      icon: 'pi pi-check-circle',
      acceptButtonStyleClass: 'p-button-success p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: async () => {
        this.confirmationService.close();
        await this.saveExamsToBackend(exams, sections, sectionQuestions, addNewQuestions);
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  parseSheet(sheet: any[][], config: TemplateColumnConfig[]): any[] {
    if (!sheet || sheet.length < 2) return [];
    const headers = sheet[0];
    return sheet
      .slice(1)
      .filter((row) => {
        return row.some((cell, index) => {
          if (cell === undefined || cell === null || cell === '') return false;
          const header = headers[index];
          return !header?.includes('Auto-calculated');
        });
      })
      .map((row) => {
        const obj: any = {};
        headers.forEach((header: string, i: number) => {
          obj[header] = row[i];
        });
        return obj;
      });
  }
  async onPublishExam(exam: any): Promise<void> {
    if (!exam.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No exam ID available',
      });
      return;
    }

    if (exam.isPublish) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Already Published',
        detail: 'This exam is already published',
      });
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to publish the exam "${exam.name}"? Once published, the exam cannot be deleted.`,
      header: 'Confirm Publish',
      icon: 'pi pi-question-circle',
      acceptButtonStyleClass: 'p-button-success',
      accept: async () => {
        this.confirmationService.close();
        try {
          this.messageService.add({
            severity: 'info',
            summary: 'Publishing Exam',
            detail: 'Publishing exam...',
          });

          const examDetails = await this.crud.get(`exams/${exam.id}`);
          if (!examDetails) {
            throw new Error('Failed to fetch exam details');
          }

          const updatedExam = {
            ...examDetails,
            isPublish: true,
          };

          delete updatedExam.totalQuestions;

          const response = await this.crud.update(`exams/${exam.id}`, updatedExam);

          if (response) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Exam published successfully!',
            });

            this.getExamDetails();
          } else {
            throw new Error('Failed to publish exam');
          }
        } catch (error) {
          console.error('Publish failed:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Publish Failed',
            detail: 'An error occurred while publishing the exam.',
          });
        }
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }
  parseExcelDate(value: any): Date | null {
    if (value instanceof Date) return value;
    if (typeof value === 'number') {
      const utc_days = Math.floor(value - 25569);
      const utc_value = utc_days * 86400;
      return new Date(utc_value * 1000);
    }
    if (typeof value === 'string') {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return null;
  }

  checkQuizDeadline(date: string): boolean {
    let currentDate = new Date();
    let deadlineDate = new Date(date);
    return currentDate > deadlineDate;
  }

  async saveExamsToBackend(
    exams: any[],
    sections: any[],
    sectionQuestions: any[],
    addNewQuestions: any[],
  ) {
    let successCount = 0;
    let errorCount = 0;
    const now = new Date().toISOString();

    for (const q of addNewQuestions) {
      try {
        const questionDto = {
          title: q['Title'],
          fullText: q['Full Text'] || '',
          category: q['Category'] || '',
          level: q['Difficulty'] || 'Easy',
          tags: q['Tags'] ? q['Tags'].split(',').map((t: string) => t.trim()) : [],
          subject: this.subjectList.find((s) => s.name === q['Subject'])?.id,
          options: {
            A: q['Option A'],
            B: q['Option B'],
            C: q['Option C'],
            D: q['Option D'],
          },
          answerOption: q['Answer Option'],
          solution: q['Solution'] || '',
          createdAt: now,
          updatedAt: now,
        };
        const created = await this.crud.post('questions', questionDto);
        this.allQuestions.push(created);
      } catch (err) {
        console.error('Failed to create question from Excel:', q['Title'], err);
      }
    }

    for (const exam of exams) {
      const teacher = this.teachers.find(
        (t) => t.firstName + ' ' + t.lastName === exam['Teachers'],
      );
      const teacherIds = teacher?.id ? [teacher.id] : [];
      const examSections = sections;

      const sectionQuestionCounts: Record<string, number> = {};

      for (const q of sectionQuestions) {
        const sectionName = q['Section Name'];
        sectionQuestionCounts[sectionName] = (sectionQuestionCounts[sectionName] || 0) + 1;
      }

      for (const q of addNewQuestions) {
        const sectionName = q['Section Name'];
        sectionQuestionCounts[sectionName] = (sectionQuestionCounts[sectionName] || 0) + 1;
      }

      const mappedSections = examSections.map((section, idx) => {
        const excelSubject = section['Subject'];
        const subject = this.subjectList.find((s) => s.name === excelSubject);
        const sectionName = section['Section Name'] || '';
        const numberOfQuestions = sectionQuestionCounts[sectionName] || 0;
        return {
          name: section['Section Name'] || '',
          subject: subject?.id,
          numberOfQuestions: numberOfQuestions,
          marksForCorrectAns: Number(section['Marks for Correct Answer'] ?? 0),
          marksForIncorrectAns: Number(section['Marks for Incorrect Answer'] ?? 0),
          totalSectionMarks:
            Number(section['Number of Questions'] ?? 0) *
            Number(section['Marks for Correct Answer'] ?? 0),
          order: idx + 1,
        };
      });

      const totalMarks = mappedSections.reduce((sum, section) => {
        return sum + section.totalSectionMarks;
      }, 0);

      const totalQuestions = mappedSections.reduce((sum, section) => {
        return sum + section.numberOfQuestions;
      }, 0);

      let startDate = exam['Start Date'];
      const parsedStartDate = this.parseExcelDate(startDate);
      if (!parsedStartDate || isNaN(parsedStartDate.getTime())) {
        errorCount++;
        console.error(`Invalid start date for exam: ${exam['Exam Name']}`);
        continue;
      }
      let deadlineDate = exam['Deadline Date'];
      const parsedDeadlineDate = this.parseExcelDate(deadlineDate);
      if (!parsedDeadlineDate || isNaN(parsedDeadlineDate.getTime())) {
        errorCount++;
        console.error(`Invalid deadline date for exam: ${exam['Exam Name']}`);
        continue;
      }

      const mappedExam = {
        name: exam['Exam Name'] || '',
        teachers: teacherIds,
        time: Number(exam['Exam Time (minutes)'] ?? 0),
        startDate: parsedStartDate.toISOString(),
        deadlineDate: parsedDeadlineDate.toISOString(),
        isPublish: exam['Is Published']?.toLowerCase() === 'yes',
        totalMarks: totalMarks,
        totalQuestions: totalQuestions,
        numberOfSections: mappedSections.length,
        sections: mappedSections,
      };

      try {
        const createdExam = await this.crud.post('exams', mappedExam);
        for (const section of mappedExam.sections) {
          const createdSection = createdExam.sections.find((s: any) => s.name === section.name);
          if (!createdSection) {
            console.error('Section not found in created exam:', section.name);
            continue;
          }

          const sectionExcelQuestions = sectionQuestions.filter(
            (q) => q['Section Name'] === section.name,
          );

          const newSectionQuestions = addNewQuestions.filter(
            (q) => q['Section Name'] === section.name,
          );

          const questionIds: string[] = [];

          for (const q of sectionExcelQuestions) {
            const found = this.allQuestions.find((dbQ) => dbQ.title === q['Question Title']);
            if (found) {
              questionIds.push(found.id);
            } else {
              console.error('Question not found in DB:', q['Question Title']);
            }
          }

          for (const q of newSectionQuestions) {
            const found = this.allQuestions.find((dbQ) => dbQ.title === q['Title']);
            if (found && !questionIds.includes(found.id)) {
              questionIds.push(found.id);
            }
          }
          if (questionIds.length > 0) {
            const examQuestionsDto = {
              examId: createdExam.id,
              sectionId: createdSection.id,
              questionIds,
            };
            await this.crud.post('exam-questions', examQuestionsDto);
          }
        }
        successCount++;
      } catch (err) {
        errorCount++;
        console.error('Error uploading exam:', mappedExam, err);
      }
    }
    this.confirmationService.close();
    if (errorCount === 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `${successCount} exams uploaded successfully!`,
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Partial Success',
        detail: `${successCount} exams uploaded, ${errorCount} failed.`,
      });
    }
    this.getExamDetails();
  }

  onViewDashboard(exam: any) {
    if (!exam.isPublish) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Dashboard Not Available',
        detail: 'Dashboard is only available for published exams.',
        life: 3000,
      });
      return;
    }

    this.router.navigate([APP_ROUTING['EXAM_ANALYSIS'], exam.id]);
  }

  showMenu(event: Event, exam: any) {
    this.currentMenuItems = [
      {
        label: exam.isPublish ? 'Already published' : 'Publish Exam',
        icon: 'pi pi-send',
        command: () => this.onPublishExam(exam),
        disabled: exam.isPublish,
        buttonClass: exam.isPublish
          ? 'p-button-rounded p-button-text p-button-sm'
          : 'p-button-rounded p-button-text p-button-success p-button-sm',
        tooltip: exam.isPublish ? 'Already published' : 'Click to publish',
        textColor: '#888888',
      },
      {
        label: exam.isPublish ? 'View Dashboard' : 'Dashboard available when published',
        icon: 'pi pi-chart-bar',
        command: () => this.onViewDashboard(exam),
        disabled: !exam.isPublish,
        buttonClass: exam.isPublish
          ? 'p-button-rounded p-button-text p-button-info p-button-sm'
          : 'p-button-rounded p-button-text p-button-sm',
        tooltip: exam.isPublish ? 'View exam dashboard' : 'Publish exam to enable dashboard',
        textColor: '#888888',
      },
      {
        label: exam.isPublish ? 'View Exam Preview' : '',
        icon: 'pi pi-eye',
        command: () => this.onTakeExam(exam),
        disabled: !exam.isPublish,
        buttonClass: exam.isPublish
          ? 'p-button-rounded p-button-text p-button-info p-button-sm'
          : 'p-button-rounded p-button-text p-button-sm',
        tooltip: exam.isPublish ? 'View exam preview' : 'Publish exam to see preview',
        textColor: '#888888',
      },
    ];

    this.globalMenu.toggle(event);
  }
}
